import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, Plus, Trash2, X, GripVertical, ShieldAlert, Shield, FileText, Wrench, Smartphone, Users, Gavel } from 'lucide-react'
import api from '../../services/api'

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'legal',         label: 'Legal / Compliance',     icon: Gavel,       colour: 'var(--red)' },
  { value: 'documentation', label: 'Documentation',          icon: FileText,    colour: 'var(--blue)' },
  { value: 'safety',        label: 'Safety Induction',       icon: Shield,      colour: 'var(--green)' },
  { value: 'role_training', label: 'Role Specific Training', icon: Wrench,      colour: 'var(--hseq-safety)' },
  { value: 'system_access', label: 'System Access',          icon: Smartphone,  colour: 'var(--purple)' },
  { value: 'meet_team',     label: 'Meet the Team',          icon: Users,       colour: 'var(--teal)' },
]

const CAT_MAP: Record<string, typeof CATEGORIES[0]> = Object.fromEntries(CATEGORIES.map(c => [c.value, c]))

const TIMINGS = [
  { value: 'before_day1', label: 'Before Day 1' },
  { value: 'day1',        label: 'Day 1' },
  { value: 'week1',       label: 'Week 1' },
  { value: 'month1',      label: 'Month 1' },
  { value: 'ongoing',     label: 'Ongoing' },
]

const RESPONSIBLE = [
  { value: 'hr',         label: 'HR' },
  { value: 'safety',     label: 'Safety' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'worker',     label: 'Worker' },
  { value: 'system',     label: 'System (auto)' },
]

const EVIDENCE = [
  { value: 'none',     label: 'None required' },
  { value: 'document', label: 'Document upload' },
  { value: 'signoff',  label: 'Sign-off' },
  { value: 'both',     label: 'Both document + sign-off' },
]

const TIMING_ORDER = ['before_day1', 'day1', 'week1', 'month1', 'ongoing']

const EMPTY_ITEM = {
  name: '', description: '', category: 'safety', mandatory: true, blocking: false,
  evidence_type: 'none', sighting_only: false, responsible: 'supervisor', due_timing: 'day1',
  reminder_days: 3, duration_minutes: '', links_to_competency: false,
}

// ── Item form modal ────────────────────────────────────────────────────────────

function ItemModal({ item, onSave, onClose }: {
  item: any; onSave: (data: any) => void; onClose: () => void
}) {
  const [form, setForm] = useState<any>({ ...EMPTY_ITEM, ...item })
  const [saving, setSaving] = useState(false)
  const f = (k: string, v: any) => setForm((prev: any) => ({ ...prev, [k]: v }))

  const save = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    await onSave(form)
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 560, padding: 24, display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{item.id ? 'Edit Item' : 'Add Checklist Item'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>

        {/* Name */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>ITEM NAME *</label>
          <input value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Electrical licence verified" style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>DESCRIPTION</label>
          <textarea rows={2} value={form.description} onChange={e => f('description', e.target.value)} placeholder="What does this involve? What does the worker need to do?" style={{ width: '100%', resize: 'none', boxSizing: 'border-box' }} />
        </div>

        {/* Category */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>CATEGORY</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {CATEGORIES.map(cat => {
              const Icon = cat.icon
              const sel = form.category === cat.value
              return (
                <div key={cat.value} onClick={() => f('category', cat.value)} style={{
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1.5px solid ${sel ? cat.colour : 'var(--border)'}`,
                  background: sel ? `color-mix(in srgb, ${cat.colour} 12%, transparent)` : 'var(--bg)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Icon size={14} style={{ color: cat.colour, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: sel ? cat.colour : 'var(--text-secondary)' }}>{cat.label}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Row: Due timing + Responsible */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>DUE TIMING</label>
            <select value={form.due_timing} onChange={e => f('due_timing', e.target.value)} style={{ width: '100%' }}>
              {TIMINGS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>RESPONSIBLE</label>
            <select value={form.responsible} onChange={e => f('responsible', e.target.value)} style={{ width: '100%' }}>
              {RESPONSIBLE.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>

        {/* Evidence */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>EVIDENCE REQUIRED</label>
          <select value={form.evidence_type} onChange={e => { f('evidence_type', e.target.value); if (e.target.value === 'none') f('sighting_only', false) }} style={{ width: '100%' }}>
            {EVIDENCE.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          {(form.evidence_type === 'document' || form.evidence_type === 'both') && (
            <div onClick={() => f('sighting_only', !form.sighting_only)} style={{
              marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
              borderRadius: 8, cursor: 'pointer',
              background: form.sighting_only ? 'var(--amber-light)' : 'var(--bg)',
              border: `1.5px solid ${form.sighting_only ? 'var(--amber)' : 'var(--border)'}`,
            }}>
              <input type="checkbox" checked={!!form.sighting_only} onChange={() => {}} style={{ flexShrink: 0, marginTop: 1, accentColor: 'var(--amber)' }} />
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: form.sighting_only ? 'var(--amber)' : 'var(--text)', margin: 0 }}>Sighting only — document does not need to be stored</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>Upload still required unless worker is employed by an onboarded contractor</p>
              </div>
            </div>
          )}
        </div>

        {/* Row: Duration + Reminder */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>DURATION (minutes)</label>
            <input type="number" min="0" value={form.duration_minutes} onChange={e => f('duration_minutes', e.target.value ? parseInt(e.target.value) : '')} placeholder="e.g. 60" style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>REMINDER (days before)</label>
            <input type="number" min="0" value={form.reminder_days} onChange={e => f('reminder_days', parseInt(e.target.value) || 0)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { key: 'mandatory', label: 'Mandatory', desc: 'Worker must complete this item' },
            { key: 'blocking', label: 'Blocking — cannot start work until complete', desc: 'System will prevent work assignment if incomplete', accent: true },
            { key: 'links_to_competency', label: 'Creates competency record when completed', desc: 'Automatically adds to competency register' },
          ].map(({ key, label, desc, accent }) => (
            <div key={key} onClick={() => f(key, !form[key])} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              borderRadius: 8, cursor: 'pointer',
              background: form[key] ? (accent ? 'var(--red-light)' : 'var(--accent-light)') : 'var(--bg)',
              border: `1.5px solid ${form[key] ? (accent ? 'var(--red)' : 'var(--accent)') : 'var(--border)'}`,
            }}>
              <input type="checkbox" checked={!!form[key]} onChange={() => {}} style={{ flexShrink: 0, accentColor: accent ? 'var(--red)' : 'var(--accent)' }} />
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: form[key] ? (accent ? 'var(--red)' : 'var(--accent)') : 'var(--text)', margin: 0 }}>{label}</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 2 }} onClick={save} disabled={!form.name.trim() || saving}>
            {saving ? 'Saving…' : item.id ? 'Update Item' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Item card ──────────────────────────────────────────────────────────────────

function ItemCard({ item, onEdit, onDelete }: { item: any; onEdit: () => void; onDelete: () => void }) {
  const cat = CAT_MAP[item.category] || CAT_MAP.safety
  const Icon = cat.icon
  return (
    <div style={{
      background: 'var(--white)', border: `1px solid ${item.blocking ? 'var(--red)' : 'var(--border)'}`,
      borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <GripVertical size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2, cursor: 'grab' }} />
      <Icon size={16} style={{ color: cat.colour, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{item.name}</p>
          {item.blocking === 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--red-light)', color: 'var(--red)', fontWeight: 700 }}>BLOCKING</span>}
          {item.mandatory !== 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>optional</span>}
          {item.links_to_competency === 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--green-light)', color: 'var(--green)', fontWeight: 600 }}>→ competency</span>}
          {item.sighting_only === 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--amber-light)', color: 'var(--amber)', fontWeight: 600 }}>sighting only</span>}
        </div>
        {item.description && <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '3px 0 0' }}>{item.description}</p>}
        <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{RESPONSIBLE.find(r => r.value === item.responsible)?.label}</span>
          {item.evidence_type !== 'none' && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>· {EVIDENCE.find(e => e.value === item.evidence_type)?.label}</span>}
          {item.duration_minutes && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>· {item.duration_minutes} min</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button onClick={onEdit} className="btn btn-secondary" style={{ padding: '4px 8px', minHeight: 'auto', fontSize: 12 }}>Edit</button>
        <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 6px' }}><Trash2 size={14} /></button>
      </div>
    </div>
  )
}

// ── Main builder ───────────────────────────────────────────────────────────────

export default function OnboardingBuilder() {
  const { id } = useParams<{ id: string }>()
  const [template, setTemplate] = useState<any>(null)
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [editItem, setEditItem] = useState<any>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [headerEdit, setHeaderEdit] = useState(false)
  const [headerForm, setHeaderForm] = useState({ name: '', role_type: '', description: '' })
  const [saving, setSaving] = useState(false)

  const load = () => {
    api.get(`/onboarding/templates/${id}`).then(r => {
      setTemplate(r.data)
      setItems(r.data.items || [])
      setHeaderForm({ name: r.data.name, role_type: r.data.role_type || '', description: r.data.description || '' })
    }).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [id])

  const saveHeader = async () => {
    setSaving(true)
    await api.put(`/onboarding/templates/${id}`, headerForm)
    setHeaderEdit(false)
    setSaving(false)
    load()
  }

  const addItem = async (data: any) => {
    const payload = {
      ...data, sort_order: items.length,
      mandatory: data.mandatory ? 1 : 0,
      blocking: data.blocking ? 1 : 0,
      links_to_competency: data.links_to_competency ? 1 : 0,
      sighting_only: data.sighting_only ? 1 : 0,
      duration_minutes: data.duration_minutes || null,
    }
    await api.post(`/onboarding/templates/${id}/items`, payload)
    setShowAdd(false)
    load()
  }

  const updateItem = async (data: any) => {
    const payload = {
      ...data,
      mandatory: data.mandatory ? 1 : 0,
      blocking: data.blocking ? 1 : 0,
      links_to_competency: data.links_to_competency ? 1 : 0,
      sighting_only: data.sighting_only ? 1 : 0,
      duration_minutes: data.duration_minutes || null,
    }
    await api.put(`/onboarding/templates/${id}/items/${editItem.id}`, payload)
    setEditItem(null)
    load()
  }

  const deleteItem = async (itemId: string) => {
    if (!confirm('Remove this item?')) return
    await api.delete(`/onboarding/templates/${id}/items/${itemId}`)
    load()
  }

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>
  if (!template) return <div className="text-center py-16" style={{ color: 'var(--red)' }}>Template not found</div>

  // Group items by timing
  const grouped: Record<string, any[]> = {}
  TIMING_ORDER.forEach(t => { grouped[t] = [] })
  items.forEach(item => { const t = item.due_timing || 'day1'; if (!grouped[t]) grouped[t] = []; grouped[t].push(item) })

  const blockingCount = items.filter(i => i.blocking === 1).length
  const totalMins = items.reduce((sum, i) => sum + (i.duration_minutes || 0), 0)

  const TIMING_LABELS: Record<string, string> = {
    before_day1: 'Before Day 1', day1: 'Day 1', week1: 'Week 1', month1: 'Month 1', ongoing: 'Ongoing',
  }
  const TIMING_RESP: Record<string, string> = {
    before_day1: 'HR responsibility', day1: 'Supervisor / Safety responsibility',
    week1: 'Ongoing supervision', month1: 'Review & assessment', ongoing: 'Continuous',
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back link */}
      <Link to="/settings/onboarding" className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--accent)' }}>
        <ChevronLeft size={16} /> Onboarding Templates
      </Link>

      {/* Header card */}
      <div className="card">
        {headerEdit ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input value={headerForm.name} onChange={e => setHeaderForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name" style={{ fontWeight: 700, fontSize: 18, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }} />
            <input value={headerForm.role_type} onChange={e => setHeaderForm(f => ({ ...f, role_type: e.target.value }))} placeholder="Role type (e.g. Field Technician)" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }} />
            <textarea rows={2} value={headerForm.description} onChange={e => setHeaderForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" style={{ resize: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setHeaderEdit(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveHeader} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{template.name}</h1>
              {template.role_type && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>{template.role_type}</p>}
              {template.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 0' }}>{template.description}</p>}
              <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                <span><strong style={{ color: 'var(--text)' }}>{items.length}</strong> items</span>
                <span><strong style={{ color: 'var(--red)' }}>{blockingCount}</strong> blocking</span>
                {totalMins > 0 && <span><strong style={{ color: 'var(--text)' }}>{Math.round(totalMins / 60 * 10) / 10}h</strong> total time</span>}
              </div>
            </div>
            <button onClick={() => setHeaderEdit(true)} className="btn btn-secondary text-sm px-3 py-1.5" style={{ minHeight: 'auto', flexShrink: 0 }}>Edit Details</button>
          </div>
        )}
      </div>

      {/* Items by group */}
      {TIMING_ORDER.map(timing => (
        <div key={timing}>
          {/* Group header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px', borderRadius: '10px 10px 0 0',
            background: timing === 'before_day1' ? 'var(--red-light)' :
                        timing === 'day1' ? 'var(--accent)' :
                        timing === 'week1' ? 'var(--amber-light)' :
                        timing === 'month1' ? 'var(--green-light)' : 'var(--bg)',
            border: `1px solid ${timing === 'day1' ? 'var(--accent)' : 'var(--border)'}`,
            marginBottom: grouped[timing].length > 0 ? 0 : 4,
          }}>
            <div>
              <p style={{ fontWeight: 700, fontSize: 14, margin: 0, color: timing === 'day1' ? 'white' : 'var(--text)' }}>{TIMING_LABELS[timing]}</p>
              <p style={{ fontSize: 11, margin: 0, color: timing === 'day1' ? 'rgba(255,255,255,0.75)' : 'var(--text-muted)' }}>{TIMING_RESP[timing]}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, color: timing === 'day1' ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)' }}>{grouped[timing].length} items</span>
              <button
                onClick={() => { setShowAdd(false); setEditItem(null); setShowAdd(true); setEditItem({ ...EMPTY_ITEM, due_timing: timing }) }}
                style={{
                  background: timing === 'day1' ? 'rgba(255,255,255,0.2)' : 'var(--white)',
                  border: `1px solid ${timing === 'day1' ? 'rgba(255,255,255,0.3)' : 'var(--border)'}`,
                  color: timing === 'day1' ? 'white' : 'var(--text-secondary)',
                  borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                <Plus size={12} /> Add
              </button>
            </div>
          </div>

          {grouped[timing].length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {grouped[timing].map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onEdit={() => setEditItem(item)}
                  onDelete={() => deleteItem(item.id)}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Global add button */}
      <button onClick={() => setEditItem({ ...EMPTY_ITEM })} className="btn btn-secondary w-full flex items-center justify-center gap-2">
        <Plus size={15} /> Add Checklist Item
      </button>

      {/* Modals */}
      {editItem && !editItem.id && (
        <ItemModal item={editItem} onSave={addItem} onClose={() => setEditItem(null)} />
      )}
      {editItem && editItem.id && (
        <ItemModal item={editItem} onSave={updateItem} onClose={() => setEditItem(null)} />
      )}
    </div>
  )
}
